import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Download, History } from 'lucide-react';
import { API } from '@/constants/bluemonk';

export const AdminAuditTrailSection = ({ items, headers }) => {
  const [filter, setFilter] = useState('all');

  const filtered = filter === 'all' ? items : items.filter(i => i.eventType === filter);

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/audit-trail/export${filter !== 'all' ? `?event_type=${filter}` : ''}`, {
        headers,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit_trail_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Audit trail exported');
    } catch (error) {
      toast.error('Failed to export');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900">Audit Trail</h1>
        <div className="flex items-center gap-3">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Events</SelectItem>
              <SelectItem value="submission">Submissions</SelectItem>
              <SelectItem value="approval">Approvals</SelectItem>
              <SelectItem value="revision-request">Revisions</SelectItem>
              <SelectItem value="comment">Comments</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={handleExport} variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <Card className="p-8 text-center">
          <History className="h-12 w-12 text-slate-400 mx-auto mb-4" />
          <p className="text-lg font-medium text-slate-700">No events yet</p>
          <p className="text-slate-500">Client activity will be recorded here</p>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left p-4 font-semibold text-slate-700">Event</th>
                  <th className="text-left p-4 font-semibold text-slate-700">Actor</th>
                  <th className="text-left p-4 font-semibold text-slate-700">Item Type</th>
                  <th className="text-left p-4 font-semibold text-slate-700">Timestamp</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((event) => (
                  <tr key={event.id} className="border-b hover:bg-slate-50">
                    <td className="p-4">
                      <Badge variant="outline" className="capitalize">
                        {event.eventType.replace('-', ' ')}
                      </Badge>
                    </td>
                    <td className="p-4">
                      <div>
                        <p className="font-medium">{event.actor?.name}</p>
                        <p className="text-xs text-slate-500">{event.actor?.role}</p>
                      </div>
                    </td>
                    <td className="p-4 capitalize">{event.sourceItemType?.replace('-', ' ')}</td>
                    <td className="p-4 text-sm text-slate-600">
                      {new Date(event.timestamp).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="p-4 bg-slate-50 border-t text-sm text-slate-500">
            Showing {filtered.length} events
          </div>
        </Card>
      )}
    </div>
  );
};
